import { createSelector } from 'reselect';
import { RootState } from 'data/root.reducers';
import { getEnabledAccessFunction, getEnabledCollectionFunctions } from 'data/supportedFeatures/supportedFeatures.selectors';
import { getConfiguredAccessFunctions } from 'data/accessFunction/accessFunction.selectors';
import { SupportedFeaturesAccessFunctions, SupportedFeaturesCollectionFunctions } from 'data/supportedFeatures/supportedFeatures.types';
import {
  FormTemplateMapByKey,
  TEMPLATE_CATEGORY_CASE_OPTIONS,
  TEMPLATE_CATEGORY_WARRANT_CLASSIC,
  TEMPLATE_CATEGORY_WARRANT_WIZARD,
} from 'data/template/template.types';
import {
  RawTemplate,
  Template,
  FormTemplate,
  TEMPLATE_CATEGORY_ACCESS_FUNCTION,
  TEMPLATE_CATEGORY_COLLECTION_FUNCTION,
} from './template.types';

export const getRawTemplates = (state: RootState): RawTemplate[] => state.warrants.templates ?? [];

export const getTemplates = createSelector(
  [getRawTemplates],
  (templates: RawTemplate[]): Template[] => {
    return templates.map((entry) => ({
      ...entry,
      template: JSON.parse(entry.template),
    }));
  },
);

export const getAccessFunctionTemplates = createSelector(
  [getTemplates],
  (templates: Template[]): Template[] => {
    return templates.filter((template) => template.category === TEMPLATE_CATEGORY_ACCESS_FUNCTION);
  },
);

export const getAccessFunctionFormTemplates = createSelector(
  [getAccessFunctionTemplates],
  (templates: Template[]): FormTemplate[] => {
    return templates.map(({ template }) => template);
  },
);

export const getAccessFunctionFormTemplatesByKey = createSelector(
  [getAccessFunctionFormTemplates],
  (formTemplates: FormTemplate[]): FormTemplateMapByKey => {
    return formTemplates.reduce((previousValue, template) => ({
      ...previousValue,
      [template.key]: template,
    }), {});
  },
);

export const getEnabledAccessFunctionFormTemplatesByKey = createSelector(
  [getAccessFunctionFormTemplates, getEnabledAccessFunction],
  (
    formTemplates: FormTemplate[],
    enabledAccessFunction: SupportedFeaturesAccessFunctions,
  ): FormTemplateMapByKey => {

    return formTemplates.filter((template) => {
      return enabledAccessFunction[template.key] != null;
    }).reduce((previousValue, template) => ({
      ...previousValue,
      [template.key]: template,
    }), {});
  },
);

export const getEnabledAndConfiguredAccessFunctionFormTemplatesByKey = createSelector(
  [getAccessFunctionFormTemplates, getEnabledAccessFunction, getConfiguredAccessFunctions],
  (
    formTemplates: FormTemplate[],
    enabledAccessFunction: SupportedFeaturesAccessFunctions,
    configuredAccessFunction: SupportedFeaturesAccessFunctions,
  ): FormTemplateMapByKey => {

    return formTemplates.filter((template) => {
      return enabledAccessFunction[template.key] != null;
    }).filter((template) => {
      return configuredAccessFunction[template.key] != null;
    }).reduce((previousValue, template) => ({
      ...previousValue,
      [template.key]: template,
    }), {});
  },
);

export const getCollectionFunctionTemplates = createSelector(
  [getTemplates],
  (templates: Template[]): Template[] => {
    return templates.filter((template) => template.category === TEMPLATE_CATEGORY_COLLECTION_FUNCTION);
  },
);

export const getCollectionFunctionFormTemplates = createSelector(
  [getCollectionFunctionTemplates],
  (templates: Template[]): FormTemplate[] => {
    return templates.map(({ template }) => template);
  },
);

export const getCollectionFunctionFormTemplatesByKey = createSelector(
  [getCollectionFunctionFormTemplates],
  (formTemplates: FormTemplate[]): FormTemplateMapByKey => {
    return formTemplates.reduce((previousValue, template) => ({
      ...previousValue,
      [template.key]: template,
    }), {});
  },
);

export const getEnabledCollectionFunctionFormTemplatesByKey = createSelector(
  [getCollectionFunctionFormTemplates, getEnabledCollectionFunctions], (
    formTemplates: FormTemplate[],
    enabledCollectionFunction: SupportedFeaturesCollectionFunctions,
  ): FormTemplateMapByKey => {
    return formTemplates.filter(({ key }) => enabledCollectionFunction[key] != null)
      .reduce((previousValue, template) => ({
        ...previousValue,
        [template.key]: template,
      }), {});
  },
);

/**
 * The case options templates, kept raw: callers of this only need each template's
 * identity, and parsing every template body to get at it would be wasted work.
 */
export const getCaseOptionsTemplates = createSelector(
  [getRawTemplates],
  (templates: RawTemplate[]): RawTemplate[] => {
    return templates.filter((template) => template.category === TEMPLATE_CATEGORY_CASE_OPTIONS);
  },
);

/**
 * The warrant templates, kept raw — the counterpart of getWarrantClassicTemplates for
 * callers that only need each template's identity and would otherwise pay to parse
 * every template body to get it.
 */
export const getRawWarrantTemplates = createSelector(
  [getRawTemplates],
  (templates: RawTemplate[]): RawTemplate[] => {
    return templates.filter((template) => template.category === TEMPLATE_CATEGORY_WARRANT_CLASSIC);
  },
);

export const getWarrantWizardTemplates = createSelector(
  [getTemplates],
  (templates: Template[]): Template[] => {
    return templates.filter((template) => template.category === TEMPLATE_CATEGORY_WARRANT_WIZARD);
  },
);

export const getWarrantClassicTemplates = createSelector(
  [getTemplates],
  (templates: Template[]): Template[] => {
    return templates.filter((template) => template.category === TEMPLATE_CATEGORY_WARRANT_CLASSIC);
  },
);
